import ApiService from "../../../services/ApiService";
import { normalizeHomeV2PagedResponse } from "../../../services/HomeService";
import { getContentLanguage } from "../../../services/LangPrefs";
import Logger from "../../../services/Logger";

const BROWSE_BASE = "/home/sliding";
const PLAY_BASE = "/home/sliding-play";

/** Extract `{ story, storySliding, templates }` from decrypted sliding group payloads. */
export function normalizeHomeSlidingGroup(raw) {
    if (!raw || typeof raw !== "object") return null;
    if (raw.code === "FAILURE") return null;

    const extract = (o) => {
        if (!o || typeof o !== "object") return null;
        const templates = Array.isArray(o.templates) ? o.templates : [];
        const story = o.story ?? null;
        const sid = o.storyId ?? o.story_id ?? story?.id ?? null;
        if (!story && templates.length === 0 && !sid) return null;

        const ss = o.storySliding ?? o.story_sliding ?? null;
        let storySliding = null;
        if (ss && typeof ss === "object") {
            storySliding = {
                id: ss.id ?? null,
                free: ss.free === true,
                publishedAt: ss.publishedAt ?? ss.published_at ?? null,
                state: ss.state ?? null,
                disabledAt: ss.disabledAt ?? ss.disabled_at ?? null,
            };
        }

        const normalizedTemplates = templates.map((t) => {
            if (!t || typeof t !== "object") return t;
            return {
                ...t,
                gridSize: t.gridSize ?? t.grid_size ?? null,
                difficultyLevel: t.difficultyLevel ?? t.difficulty_level ?? null,
                startingLayout: t.startingLayout ?? t.starting_layout ?? null,
                createdAt: t.createdAt ?? t.created_at ?? null,
                completedByUser: t.completedByUser ?? t.completed_by_user ?? null,
            };
        });

        return {
            storyId: typeof sid === "string" ? sid.trim() : sid,
            story,
            storySliding,
            templates: normalizedTemplates,
            puzzleTemplate: o.puzzleTemplate ?? o.puzzle_template ?? null,
            playCount: (() => {
                const raw = o.playCount ?? o.play_count;
                if (raw == null || raw === "") return 0;
                const n = Number(raw);
                return Number.isFinite(n) && n >= 0 ? n : 0;
            })(),
        };
    };

    const direct = extract(raw);
    if (direct) return direct;

    const nestedKeys = ["data", "content", "payload", "body", "result", "slidingStoryGroup"];
    for (const key of nestedKeys) {
        const inner = raw[key];
        if (inner && typeof inner === "object") {
            const u = extract(inner);
            if (u) return u;
        }
    }
    return null;
}

async function listHomeFeatured(path, { language, limit = 5 } = {}) {
    try {
        const params = new URLSearchParams();
        const lang = language ?? getContentLanguage();
        if (lang != null && String(lang).trim() !== "") {
            params.set("language", String(lang).trim());
        }
        const cap = Math.max(1, Math.min(Number(limit) || 5, 20));
        params.set("limit", String(cap));
        const data = await ApiService.getData(`${BROWSE_BASE}/${path}?${params.toString()}`);
        if (data && data.code === "FAILURE") {
            return { language: lang, limit: cap, content: [] };
        }
        const content = Array.isArray(data?.content)
            ? data.content.map((row) => normalizeHomeSlidingGroup(row)).filter(Boolean)
            : [];
        return {
            language: data?.language ?? lang,
            limit: data?.limit ?? cap,
            content,
        };
    } catch (error) {
        Logger.error(`SlidingService.${path}:`, error);
        return { language: language ?? getContentLanguage(), limit: 5, content: [] };
    }
}

function buildPlayListQuery(params = {}) {
    const search = new URLSearchParams();
    search.set("page", String(params.page ?? 0));
    search.set("pageSize", String(params.pageSize ?? 10));
    search.set("sort", params.sort ?? "updatedAt:desc");
    if (params.slidingId != null && String(params.slidingId).trim() !== "") {
        search.set("slidingId", String(params.slidingId).trim());
    }
    if (params.keyword != null && String(params.keyword).trim() !== "") {
        search.set("keyword", String(params.keyword).trim());
    }
    if (params.isCompleted !== undefined && params.isCompleted !== null) {
        search.set("isCompleted", String(Boolean(params.isCompleted)));
    }
    if (params.onlyStarted !== undefined && params.onlyStarted !== null) {
        search.set("onlyStarted", String(Boolean(params.onlyStarted)));
    }
    return search.toString();
}

/**
 * Home sliding browse + play REST (`/home/sliding`, `/home/sliding-play`).
 * Same ApiService + paging patterns as {@link ../../../admin/story/AdminStoryService}.
 */
const SlidingService = {
    /**
     * GET /home/sliding — published sliding puzzles by story group (paged).
     * @param {object} params — page, pageSize, language?, free?, excludeCompleted?, excludePlayed?, sort?, keyword?, tags?
     */
    listBrowse: async ({
        page = 0,
        pageSize = 12,
        free,
        excludeCompleted,
        excludePlayed,
        language,
        sort,
        keyword,
        tags,
    } = {}) => {
        try {
            const params = new URLSearchParams({
                page: String(page),
                pageSize: String(pageSize),
            });
            if (language != null && String(language).trim() !== "") {
                params.set("language", String(language).trim());
            }
            if (free === true || free === false) {
                params.set("free", String(free));
            }
            if (excludeCompleted === true || excludeCompleted === false) {
                params.set("excludeCompleted", String(excludeCompleted));
            }
            if (excludePlayed === true || excludePlayed === false) {
                params.set("excludePlayed", String(excludePlayed));
            }
            if (sort != null && String(sort).trim() !== "") {
                params.set("sort", String(sort).trim());
            }
            const kw = typeof keyword === "string" ? keyword.trim() : "";
            if (kw) params.set("keyword", kw);
            const tagList = Array.isArray(tags) ? tags : [];
            tagList.forEach((t) => {
                if (t != null && String(t).trim() !== "") {
                    params.append("tags", String(t).trim());
                }
            });

            const data = await ApiService.getData(`${BROWSE_BASE}?${params.toString()}`);
            const paged = normalizeHomeV2PagedResponse(data);
            paged.content = paged.content
                .map((item) => normalizeHomeSlidingGroup(item) ?? item)
                .filter(Boolean);
            return paged;
        } catch (error) {
            Logger.error("SlidingService.listBrowse:", error);
            return { content: [], totalElements: 0, totalPages: 0, number: 0, size: 0, hasNext: false };
        }
    },

    /**
     * GET /home/sliding/popular — published stories ranked by completed plays (default 5).
     * @param {{ language?: string, limit?: number }} [params]
     */
    listPopular: async ({ language, limit = 5 } = {}) => listHomeFeatured("popular", { language, limit }),

    /**
     * GET /home/sliding/editors-picks — checked puzzles, newest check first (default 5).
     * @param {{ language?: string, limit?: number }} [params]
     */
    listEditorsPicks: async ({ language, limit = 5 } = {}) =>
        listHomeFeatured("editors-picks", { language, limit }),

    /**
     * GET /home/sliding/distinct-tags — `[{ tag, count }, …]` for published sliding-linked stories.
     * @param {object} params — language?, state? (default PUBLISHED)
     */
    getDistinctTags: async ({ language, state = "PUBLISHED" } = {}) => {
        try {
            const params = new URLSearchParams();
            const lang = language ?? getContentLanguage();
            if (lang != null && String(lang).trim() !== "") {
                params.set("language", String(lang).trim());
            }
            if (state != null && String(state).trim() !== "") {
                params.set("state", String(state).trim());
            }
            const qs = params.toString() ? `?${params.toString()}` : "";
            const data = await ApiService.getData(`${BROWSE_BASE}/distinct-tags${qs}`);
            if (data && data.code === "FAILURE") {
                return {
                    success: false,
                    error: data.message || "Failed to fetch sliding tags",
                    data: [],
                };
            }
            const list = Array.isArray(data) ? data : [];
            return { success: true, data: list };
        } catch (error) {
            Logger.error("SlidingService.getDistinctTags:", error);
            return {
                success: false,
                error: error.message || "Failed to fetch sliding tags",
                data: [],
            };
        }
    },

    /**
     * GET /home/sliding/newest — newest published story group (SlidingStoryGroupRo)
     * with all templates for that story (all grid sizes / difficulties).
     * @param {{ language?: string, excludeCompleted?: boolean }} [params]
     */
    getNewest: async ({ language, excludeCompleted = true } = {}) => {
        try {
            const params = new URLSearchParams();
            const lang = language ?? getContentLanguage();
            if (lang != null && String(lang).trim() !== "") {
                params.set("language", String(lang).trim());
            }
            if (excludeCompleted === true || excludeCompleted === false) {
                params.set("excludeCompleted", String(excludeCompleted));
            }
            const qs = params.toString() ? `?${params.toString()}` : "";
            const response = await ApiService.getData(`${BROWSE_BASE}/newest${qs}`);
            if (response && (response.code === "FAILURE" || response.code === "NO_DATA")) {
                return {
                    success: false,
                    error: response.message || "No newest sliding puzzle",
                    data: null,
                };
            }
            const group = normalizeHomeSlidingGroup(response) ?? response;
            return { success: true, data: group };
        } catch (error) {
            Logger.error("SlidingService.getNewest:", error);
            return {
                success: false,
                error: error.message || "Failed to fetch newest sliding story",
                data: null,
            };
        }
    },

    /** GET /home/sliding/{slidingId} — single published sliding template (SlidingRo). */
    getById: async (slidingId) => {
        try {
            if (!slidingId || String(slidingId).trim() === "") {
                return { success: false, error: "Sliding id required", data: null };
            }
            const response = await ApiService.getData(
                `${BROWSE_BASE}/${encodeURIComponent(slidingId)}`
            );
            if (response && (response.code === "FAILURE" || response.code === "NO_DATA")) {
                return {
                    success: false,
                    error: response.message || "Sliding puzzle not found",
                    data: null,
                };
            }
            return { success: true, data: response };
        } catch (error) {
            Logger.error("SlidingService.getById:", error);
            return {
                success: false,
                error: error.message || "Failed to fetch sliding puzzle",
                data: null,
            };
        }
    },

    /** GET /home/sliding/{storyId}/story — single SlidingStoryGroupRo for play. */
    getStoryGroup: async (storyId) => {
        try {
            if (!storyId || String(storyId).trim() === "") return null;
            const raw = await ApiService.getData(
                `${BROWSE_BASE}/${encodeURIComponent(storyId)}/story`
            );
            return normalizeHomeSlidingGroup(raw);
        } catch (error) {
            Logger.error("SlidingService.getStoryGroup:", error);
            return null;
        }
    },

    /** GET /home/sliding/free/latest — latest free teaser for landing. */
    getFreeLatest: async ({ language } = {}) => {
        try {
            const lang = language ?? getContentLanguage();
            const params = new URLSearchParams();
            if (lang != null && String(lang).trim() !== "") {
                params.set("language", String(lang).trim());
            }
            const qs = params.toString() ? `?${params.toString()}` : "";
            const raw = await ApiService.getData(`${BROWSE_BASE}/free/latest${qs}`);
            return normalizeHomeSlidingGroup(raw);
        } catch (error) {
            Logger.error("SlidingService.getFreeLatest:", error);
            return null;
        }
    },

    /**
     * GET /home/sliding-play — paged play sessions (encrypted when not local).
     * @param {object} params — page, pageSize, sort, slidingId?, keyword?, isCompleted?, onlyStarted?
     */
    listPlay: async (params = {}) => {
        try {
            const qs = buildPlayListQuery(params);
            const data = await ApiService.getData(`${PLAY_BASE}?${qs}`);
            return normalizeHomeV2PagedResponse(data);
        } catch (error) {
            Logger.error("SlidingService.listPlay:", error);
            return { content: [], totalElements: 0, totalPages: 0, number: 0, size: 0, hasNext: false };
        }
    },

    /** GET /home/sliding-play/last-game — in-progress session (JWT required). */
    getLastGame: async () => {
        try {
            const data = await ApiService.getData(`${PLAY_BASE}/last-game`);
            if (data && (data.code === "FAILURE" || data.code === "NO_DATA")) {
                return { success: false, error: data.message, data: null };
            }
            return { success: true, data };
        } catch (error) {
            Logger.error("SlidingService.getLastGame:", error);
            return {
                success: false,
                error: error.message || "Failed to fetch last game",
                data: null,
            };
        }
    },

    /**
     * POST /home/sliding-play/start — begin session for published sliding template.
     * @param {string} slidingId
     */
    startPlay: async (slidingId) => {
        try {
            if (!slidingId) {
                return { success: false, error: "Sliding id required", data: null };
            }
            const data = await ApiService.postData(`${PLAY_BASE}/start`, { slidingId });
            if (data && data.code === "FAILURE") {
                return { success: false, error: data.message || "Failed to start", data: null };
            }
            return { success: true, data };
        } catch (error) {
            Logger.error("SlidingService.startPlay:", error);
            return {
                success: false,
                error: error.message || "Failed to start sliding play",
                data: null,
            };
        }
    },

    /** POST /home/sliding-play/restart */
    restartPlay: async (slidingId) => {
        try {
            if (!slidingId) {
                return { success: false, error: "Sliding id required", data: null };
            }
            const data = await ApiService.postData(`${PLAY_BASE}/restart`, { slidingId });
            if (data && data.code === "FAILURE") {
                return { success: false, error: data.message || "Failed to restart", data: null };
            }
            return { success: true, data };
        } catch (error) {
            Logger.error("SlidingService.restartPlay:", error);
            return {
                success: false,
                error: error.message || "Failed to restart sliding play",
                data: null,
            };
        }
    },

    /**
     * PUT /home/sliding-play/progress — save layout / completion.
     * @param {string} slidingId
     * @param {{ currentLayout?: number[], isCompleted?: boolean, tileMode?: string }} payload
     */
    progressPlay: async (slidingId, { currentLayout, isCompleted, tileMode } = {}) => {
        try {
            if (!slidingId) {
                return { success: false, error: "Sliding id required", data: null };
            }
            const body = { slidingId };
            if (currentLayout != null) body.currentLayout = currentLayout;
            if (isCompleted !== undefined) body.isCompleted = isCompleted;
            const mode = typeof tileMode === "string" ? tileMode.trim().toLowerCase() : "";
            if (mode === "image" || mode === "numbers") body.tileMode = mode;
            const data = await ApiService.putData(`${PLAY_BASE}/progress`, body);
            if (data && data.code === "FAILURE") {
                return { success: false, error: data.message || "Failed to save progress", data: null };
            }
            return { success: true, data };
        } catch (error) {
            const status = error?.status;
            const serverMsg =
                (typeof error?.response?.error === "string" && error.response.error) ||
                (typeof error?.response?.message === "string" && error.response.message) ||
                error?.message ||
                "";
            // Expected when the play row is already finished — callers should stop syncing.
            if (
                status === 400 &&
                /already been completed/i.test(String(serverMsg))
            ) {
                return {
                    success: false,
                    alreadyCompleted: true,
                    error: serverMsg,
                    data: null,
                };
            }
            Logger.error("SlidingService.progressPlay:", error);
            return {
                success: false,
                error: serverMsg || "Failed to save sliding progress",
                data: null,
            };
        }
    },
};

export default SlidingService;
